import { makeAutoObservable } from "mobx"

import {
  AddProductRequest,
  Attribute,
  Product,
  ProductDetails,
  ProductListQueryRequest,
  ProductListResponsePaginationMetaData,
  ProductRelationshipType,
  LinkProductsData,
} from "@framework/types/product"
import productsService from "@services/products.service"
import { ConnectorDocument } from "@framework/types/content-manager"
import { SortBy } from "@framework/types/common"

export interface Company {
  id: string
  name: string
}

export interface ProductCategory {
  id: string
  name: string
  companyId?: string
  child?: ProductCategory
}

export interface V2_Products {
  id: string
  name: string
  description: string
  company: Company
  categories: ProductCategory[]
  contentCount: number
  relationshipType?: ProductRelationshipType
  notes?: string
}

interface ProductFilter {
  identifiers: ProductCategory[]
  manufacturers: Company[]
  includes?: boolean
}

export default class ProductStore {
  constructor() {
    makeAutoObservable(this)
  }

  searchQuery: string = ""

  orderBy: SortBy | null = {
    name: "name",
    direction: true,
  }

  activeFilter: ProductFilter = {
    identifiers: [],
    manufacturers: [],
    includes: true,
  }

  activePage: number = 1

  product: Product | null = null

  error: string | null = null

  addProductError: string | null = null

  isLoading = false

  isProductDetailsLoading = false

  isUpdateProductLoading = false

  deletingProducts: { [key: string]: boolean } = {}

  isContentsLoading = false

  addingProduct = false

  products: V2_Products[] = []

  productsCache: Map<string, V2_Products> = new Map()

  productsMetaData: ProductListResponsePaginationMetaData = {
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  }

  loadingProductsList = true

  productListSignal: AbortController = new AbortController()

  errorProductsList: string | null = null

  productDetails: ProductDetails | null = null

  contentPaginationMeta: {
    page: number
    pageSize: number
    totalPages: number
  } | null = null

  productContents: ConnectorDocument[] = []

  linkProductRelationShipModalData = {
    products: [] as V2_Products[],
    notes: "",
    type: "",
    addModalShowed: false,
  }

  attributeError: string | null = null

  isAddingAttribute = false

  isUpdatingAttribute = false

  isDeletingAttribute = false

  isLinkingProduct = false

  isUpdatingLinkProduct = false

  isDeletingLinkProduct = false

  linkedProducts: LinkProductsData[] = []

  getProductById = (id: string) => {
    return this.productsCache.get(id) ?? null
  }

  loadProducts = async (data: ProductListQueryRequest, reset = true) => {
    const {
      filters,
      pagination: { pageSize = 15, ...restPagination },
      ...rest
    } = data

    // Avoid abort storms: SearchWidget remounts often re-request the same empty catalog.
    const searchKey = String(filters?.search ?? "")
    if (
      !reset &&
      this.products.length > 0 &&
      searchKey === "" &&
      pageSize >= (this.productsMetaData?.pageSize || 0)
    ) {
      return
    }
    if (
      this.products.length > 0 &&
      searchKey === "" &&
      pageSize === 500 &&
      this.loadingProductsList
    ) {
      return
    }
    if (
      this.products.length >= 100 &&
      searchKey === "" &&
      pageSize === 500 &&
      !this.loadingProductsList
    ) {
      return
    }

    this.loadingProductsList = true
    this.errorProductsList = null

    try {
      if (reset) this.products = []

      this.productListSignal.abort("AbortError")

      this.productListSignal = new AbortController()

      const response = await productsService.getAllProductsList(
        {
          filters,
          pagination: {
            pageSize,
            ...restPagination,
          },
          ...rest,
        },
        this.productListSignal.signal
      )

      response.data.data.forEach((it) => this.productsCache.set(it.id, it))
      this.products = response.data.data
      this.productsMetaData = response.data.pagination
      this.loadingProductsList = false
    } catch (error) {
      console.log({ error })
      if (
        error instanceof Error &&
        (error.name === "AbortError" || error.name === "CanceledError")
      ) {
        return
      }

      this.errorProductsList = "Failed to load products"
      this.loadingProductsList = false
    }
  }

  addProduct = async (productsData: AddProductRequest[]) => {
    this.addingProduct = true
    this.addProductError = null

    try {
      const response = await productsService.createProduct({
        products: productsData,
      })

      this.addProductError = response.data.errors[0]?.message

      return response.data
    } catch (error) {
      this.addProductError = "Failed to add product"
    } finally {
      this.addingProduct = false
    }

    return { success: [], errors: [] }
  }

  updateSearchQuery = (query: string) => {
    this.searchQuery = query
  }

  updateActivePage = (page: number) => {
    this.activePage = page
  }

  updateActiveFilter = (filter: ProductFilter) => {
    this.activeFilter = filter
  }

  updateOrder = (sortBy: SortBy | null) => {
    this.orderBy = sortBy
  }

  addAttribute = async (
    productId: string,
    attribute: Attribute
  ): Promise<(Attribute & { product: V2_Products }) | null> => {
    this.isAddingAttribute = true
    this.attributeError = null

    try {
      const response = await productsService.addAttribute(productId, attribute)
      return response.data
    } catch (error) {
      this.attributeError = "Failed to add attribute"
    } finally {
      this.isAddingAttribute = false
    }
    return null
  }

  updateAttribute = async (
    productId: string,
    attribute: Attribute
  ): Promise<Attribute | null> => {
    this.isUpdatingAttribute = true
    this.attributeError = null

    try {
      const response = await productsService.updateAttribute(
        productId,
        attribute
      )
      return response.data
    } catch (error) {
      this.attributeError = "Failed to update attribute"
    } finally {
      this.isUpdatingAttribute = false
    }
    return null
  }

  deleteAttribute = async (
    productId: string,
    attributeId: string
  ): Promise<boolean> => {
    this.isDeletingAttribute = true
    this.attributeError = null

    try {
      const response = await productsService.deleteAttribute(
        productId,
        attributeId
      )
      return response.status === 200
    } catch (error) {
      this.attributeError = "Failed to delete attribute"
    } finally {
      this.isDeletingAttribute = false
    }
    return false
  }
}
